// src/utils/grapeIndex.js
// Loads the grape-registered EPA number index from Firestore
// Seeded by the seedGrapeIndex Firebase Function
// Used to show whether a product is labeled for use on grapes in WA/OR

import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

let _waGrapeEpas = null;
let _orGrapeEpas = null;
let _loaded = false;
let _loading = null;

export async function loadGrapeIndex() {
  if (_loaded) return;
  if (_loading) return _loading;

  _loading = (async () => {
    try {
      const [waSnap, orSnap] = await Promise.all([
        getDoc(doc(db, 'grape_index', 'washington')),
        getDoc(doc(db, 'grape_index', 'oregon')),
      ]);

      if (waSnap.exists()) {
        _waGrapeEpas = new Set(waSnap.data().epaNumbers || []);
        console.log(`Grape index loaded — WA: ${_waGrapeEpas.size} EPA numbers`);
      } else {
        _waGrapeEpas = new Set();
        console.warn('Grape index not found for WA — run seedGrapeIndex from Admin Panel');
      }

      if (orSnap.exists()) {
        _orGrapeEpas = new Set(orSnap.data().epaNumbers || []);
        console.log(`Grape index loaded — OR: ${_orGrapeEpas.size} EPA numbers`);
      } else {
        _orGrapeEpas = new Set();
        console.warn('Grape index not found for OR — run seedGrapeIndex from Admin Panel');
      }

      _loaded = true;
    } catch (err) {
      console.warn('Could not load grape index:', err.message);
      _waGrapeEpas = new Set();
      _orGrapeEpas = new Set();
      _loaded = true;
    }
  })();

  return _loading;
}

// Check if an EPA number is labeled for grapes in WA and/or OR
export function getGrapeStatus(epaNumber) {
  if (!epaNumber) return { wa: null, or: null };

  const epa = epaNumber.trim();

  // If index not loaded yet, return null (unknown)
  if (!_loaded) return { wa: null, or: null };

  // Empty set means index exists but product not in it
  const waIndexed = _waGrapeEpas !== null;
  const orIndexed = _orGrapeEpas !== null;

  return {
    wa: waIndexed ? _waGrapeEpas.has(epa) : null,
    or: orIndexed ? _orGrapeEpas.has(epa) : null,
    indexed: waIndexed || orIndexed,
  };
}

export function isGrapeIndexLoaded() {
  return _loaded && (_waGrapeEpas?.size > 0 || _orGrapeEpas?.size > 0);
}
